import type { LngLatBoundsLike, Map } from 'maplibre-gl';
import { LngLat } from 'maplibre-gl';
import type { FlyToOptions, PointType } from '../map-types';
import { calculateBounds } from './calculate-bounds';
import { findMaxMinPoint } from './find-max-min-point';
import { fitBoundsToPoint } from './fit-bounds-to-point';
import { isPointInPaddedBounds } from './is-point-in-padded-bounds';
import { refitBounds } from './refit-bounds';
import { validateLatValue } from './validate-lat-value';

type MapPositionProps = {
  map: Map | null;
  points: PointType[];
  center: 'auto' | { lng: number; lat: number };
  zoom: 'auto' | number;
  flyToOptions?: FlyToOptions;
};
const DEFAULT_POSITION_OPTIONS = {
  duration: 0,
  padding: 50
} as const;

export const setMapPosition = ({
  map,
  points,
  center,
  zoom,
  flyToOptions = {}
}: MapPositionProps) => {
  const onePointZoom = zoom === 'auto' ? 10 : zoom;
  const options = {
    ...DEFAULT_POSITION_OPTIONS,
    ...flyToOptions
  };

  const bounds = calculateBounds(points.map((point) => point.coordinates));

  if (!bounds) {
    if (points?.length > 0) {
      map?.flyTo({
        zoom: onePointZoom,
        center: points[0].coordinates,
        ...options
      });
    }
    if (!points?.length) {
      // show USA
      map?.flyTo({
        zoom: 4,
        center: { lng: -102.02745886465388, lat: 40.62255702313457 },
        ...options
      });
    }
    return;
  }

  const [{ coordinates: userLocation, id }, ...pointsWithoutUserLocation] =
    points;
  const hasUserLocation = id === 'user_location';
  const coordinates = pointsWithoutUserLocation.map(
    (point) => point.coordinates
  );
  const derivedCenter = hasUserLocation ? userLocation : center;

  if (hasUserLocation && derivedCenter !== 'auto') {
    if (!map) {
      return;
    }

    const transform = map.transform.clone();
    const zoom = map.getZoom();
    transform.center = new LngLat(derivedCenter.lng, derivedCenter.lat);
    transform.zoom = zoom;

    const isAnyPointInMapViewport = coordinates.some((point) =>
      isPointInPaddedBounds(point, transform, -50)
    );

    if (!isAnyPointInMapViewport && coordinates.length) {
      const minDistancePoint = findMaxMinPoint(
        coordinates,
        derivedCenter,
        'min'
      );

      const newBounds = fitBoundsToPoint(minDistancePoint, derivedCenter);
      const { zoom } =
        map.cameraForBounds(newBounds, {
          padding: 50,
          center: derivedCenter
        }) ?? {};
      transform.zoom = zoom ?? transform.zoom;
    }

    refitBounds({
      points: coordinates,
      center: derivedCenter,
      map,
      transform,
      flyToOptions
    });
  } else if (derivedCenter === 'auto' && zoom === 'auto') {
    map?.fitBounds(bounds, {
      ...options
    });
  } else if (derivedCenter !== 'auto' && zoom === 'auto') {
    const boundsCornersPoints = [
      bounds[0],
      bounds[1],
      { lng: bounds[0].lng, lat: bounds[1].lat },
      { lng: bounds[1].lng, lat: bounds[0].lat }
    ];

    const maxDistantCornerPoint = findMaxMinPoint(
      boundsCornersPoints,
      derivedCenter,
      'max'
    );

    const fromCenterToMaxDistantPoint = {
      lng: maxDistantCornerPoint.lng - derivedCenter.lng,
      lat: maxDistantCornerPoint.lat - derivedCenter.lat
    };

    const otherSidePoint = validateLatValue({
      lat: derivedCenter.lat - fromCenterToMaxDistantPoint.lat,
      lng: derivedCenter.lng - fromCenterToMaxDistantPoint.lng
    });

    map?.fitBounds(
      [maxDistantCornerPoint, otherSidePoint] as LngLatBoundsLike,
      {
        center: derivedCenter,
        ...options
      }
    );
  } else if (derivedCenter === 'auto' && zoom !== 'auto') {
    map?.fitBounds(bounds, {
      zoom: zoom,
      ...options
    });
  } else if (derivedCenter !== 'auto' && zoom !== 'auto') {
    map?.flyTo({
      zoom: zoom,
      center: derivedCenter,
      ...options
    });
  }
};
